import React from "react";

// Student and Senior Citizen plans. Eligibility is checked by uploading an ID
// that an admin reviews. Once approved, the user pays the normal plan price
// minus the discount (the server applies the discount, never this page).

const OFFERS = [
  {
    category: "student",
    icon: "🎓",
    title: "Student",
    eligibility: "For school and college students.",
    documents: [
      "Valid school or educational institution ID card",
      "The card must be for the current academic year",
    ],
  },
  {
    category: "senior",
    icon: "🧓",
    title: "Senior Citizen",
    eligibility: "For readers aged 60 and above.",
    documents: [
      "Aadhaar Card or any valid government-issued ID",
      "Date of birth or age must be clearly visible on the document",
    ],
  },
];

const ORDER = ["monthly", "quarterly", "annual"];

const rupees = (paise) => Math.round(paise / 100);
const discounted = (paise, pct) => Math.max(100, Math.round((paise * (100 - pct)) / 100));

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-IN", { dateStyle: "medium" });
}

function OfferCard({ offer, pct, plans, verification, signedIn, busyKey, onApply, onPay, onSignIn }) {
  const status = verification?.status || "none";
  const canApply = status === "none" || status === "rejected" || status === "expired";

  return (
    <div className={`offer-card offer-${offer.category}`}>
      <div className="offer-badge">Savings available</div>

      <div className="offer-head">
        <span className="offer-icon" aria-hidden="true">{offer.icon}</span>
        <div>
          <h3>{offer.title}</h3>
          <p className="offer-eligibility">{offer.eligibility}</p>
        </div>
      </div>

      <div className="offer-discount">
        <span className="offer-pct">{pct}% off</span>
        <span className="offer-pct-sub">on Monthly, Quarterly and Annual plans</span>
      </div>

      <div className="offer-docs">
        <div className="offer-docs-title">Documents required</div>
        <ul>
          {offer.documents.map((d) => (
            <li key={d}><span className="check">✓</span>{d}</li>
          ))}
        </ul>
      </div>

      {status === "rejected" && (
        <div className="offer-note offer-note--bad">
          <strong>Your last request was not approved.</strong>
          {verification.rejectReason ? ` ${verification.rejectReason}.` : ""} Please upload the
          document again.
        </div>
      )}
      {status === "expired" && (
        <div className="offer-note offer-note--bad">
          Your verification expired. Please upload a current document to continue.
        </div>
      )}
      {status === "pending" && (
        <div className="offer-note offer-note--wait">
          <strong>Under review.</strong> We have your document. Check back here once it is approved.
        </div>
      )}

      {status === "approved" ? (
        <div className="offer-pay">
          <div className="offer-note offer-note--ok">
            <strong>Verified.</strong>
            {verification.validUntil ? ` Valid until ${fmtDate(verification.validUntil)}.` : ""} Choose your plan:
          </div>
          {plans.map((plan) => {
            const key = `${offer.category}:${plan.id}`;
            const loading = busyKey === key;
            return (
              <button
                key={plan.id}
                type="button"
                className="offer-pay-btn"
                disabled={!!busyKey}
                onClick={() => onPay(plan, offer.category)}
              >
                <span className="offer-pay-label">
                  {plan.label} <small>{plan.period}</small>
                </span>
                <span className="offer-pay-price">
                  {loading ? (
                    <span className="spinner" />
                  ) : (
                    <>
                      <s>₹{rupees(plan.amountPaise)}</s>
                      <strong>₹{rupees(discounted(plan.amountPaise, pct))}</strong>
                    </>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <button
          type="button"
          className="offer-cta"
          disabled={status === "pending"}
          onClick={() => (signedIn ? onApply(offer.category) : onSignIn(offer.category))}
        >
          {status === "pending"
            ? "Under review"
            : canApply && status !== "none"
              ? "Upload document again"
              : "Verify and apply"}
        </button>
      )}
    </div>
  );
}

export default function SpecialOffers({
  plans,
  special,
  verifications,
  signedIn,
  busyKey,
  onApply,
  onPay,
  onSignIn,
}) {
  const standard = ORDER.map((id) => plans.find((p) => p.id === id)).filter(Boolean);

  return (
    <section className="offers-section">
      <div className="section-label">Special offers</div>
      <h2 className="offers-title">Student and Senior Citizen plans</h2>
      <p className="offers-sub">
        Upload your ID once. After our team approves it, you pay a lower price on every JB+ plan.
      </p>
      <div className="offers-grid">
        {OFFERS.map((offer) => (
          <OfferCard
            key={offer.category}
            offer={offer}
            pct={Number(special?.[offer.category]?.discountPercent) || 0}
            plans={standard}
            verification={verifications?.[offer.category]}
            signedIn={signedIn}
            busyKey={busyKey}
            onApply={onApply}
            onPay={onPay}
            onSignIn={onSignIn}
          />
        ))}
      </div>
    </section>
  );
}
